import React, { useRef, useEffect, useState } from 'react';
import { Typography } from '../../../interfaces/typography';
import { TranscriptRow } from './TranscriptRow';
import { SEC_TO_MS } from '../../../cfg/const';
import { SessionDetailView } from '../../History/views';
import { SessionDataResult } from 'interfaces/sessions';

interface TranscriptBodyProps {
  data: SessionDataResult;  
  currentTimeInMs: number;
  isPlaying: boolean;
  updateSelectedTranscript?: (selectedIdx: number | undefined) => void;
  selectedTranscriptIdx?: number;
  view: SessionDetailView;
}

const NoTranscripts: React.FC = () => (
  <div className="flex-1 flex flex-col items-center justify-center h-full">
    <div className="flex flex-col items-center gap-2 p-4 text-center">
      <div className="w-10 h-10 text-gray-300">
        <svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
          <path
            d="M8 12H16M8 16H12M7.8 21H16.2C17.8802 21 18.7202 21 19.362 20.673C19.9265 20.3854 20.3854 19.9265 20.673 19.362C21 18.7202 21 17.8802 21 16.2V7.8C21 6.11984 21 5.27976 20.673 4.63803C20.3854 4.07354 19.9265 3.6146 19.362 3.32698C18.7202 3 17.8802 3 16.2 3H7.8C6.11984 3 5.27976 3 4.63803 3.32698C4.07354 3.6146 3.6146 4.07354 3.32698 4.63803C3 5.27976 3 6.11984 3 7.8V16.2C3 17.8802 3 18.7202 3.32698 19.362C3.6146 19.9265 4.07354 20.3854 4.63803 20.673C5.27976 21 6.11984 21 7.8 21Z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      <Typography variant="h3" color="gray">
        No Transcript Available
      </Typography>
      <Typography variant="mediumParagraph" color="gray" className="max-w-md">
        There is no transcript data available for this session.
      </Typography>
    </div>
  </div>
);

// Helper function to safely get timestamp in milliseconds
const getTimeInMs = (time: number | null): number => {
  if (time === null) return 0;
  return time*SEC_TO_MS;
};

export const TranscriptBody: React.FC<TranscriptBodyProps> = ({
  data,
  currentTimeInMs,
  isPlaying,
  updateSelectedTranscript,
  selectedTranscriptIdx,
  view
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isAutoscrollEnabled, setIsAutoscrollEnabled] = useState(true);
  const userManuallyScrolled = useRef(false);
  const lastScrollTopRef = useRef(0);
  const lastTimeRef = useRef(currentTimeInMs);
  const programmaticScrollRef = useRef(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Reset autoscroll when new transcripts are loaded
  useEffect(() => {
    setIsAutoscrollEnabled(true);
    userManuallyScrolled.current = false;
    lastTimeRef.current = currentTimeInMs;
  }, [data.turns, currentTimeInMs]);

  // Reset autoscroll when playback starts
  useEffect(() => {
    if (isPlaying) {
      setIsAutoscrollEnabled(true);
      userManuallyScrolled.current = false;
    }
  }, [isPlaying]);

  // Handle user scroll - detect and disable autoscroll during playback
  const handleScroll = () => {
    if (!containerRef.current) return;
    
    // Clear any pending timeout to prevent multiple handlers
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    
    // Use a small timeout to differentiate between user and programmatic scrolls
    scrollTimeoutRef.current = setTimeout(() => {
      // Only proceed if we have the container and we're playing
      if (!containerRef.current || !isPlaying) return;
      
      const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
      
      // If user has scrolled up (scrollTop decreased) or the position changed significantly
      const scrollDiff = scrollTop - lastScrollTopRef.current;
      const isScrollingUp = scrollDiff < -10; // Significant upward scroll
      const isLargeScroll = Math.abs(scrollDiff) > 50; // Any large scroll
      
      if (isScrollingUp || isLargeScroll) {
        userManuallyScrolled.current = true;
        setIsAutoscrollEnabled(false);
      }
      
      // If scrolled to near bottom, re-enable autoscroll
      const distanceFromBottom = scrollHeight - clientHeight - scrollTop;
      const isAtBottom = distanceFromBottom < 50;
      
      if (isAtBottom && !isAutoscrollEnabled) {
        setIsAutoscrollEnabled(true);
        userManuallyScrolled.current = false;
      }
      
      lastScrollTopRef.current = scrollTop;
    }, 50); // Short delay to catch real user scrolls
  };

  // Function to scroll to a specific transcript
  const scrollToTranscript = (transcriptIdx: number) => {
    const selectedElement = document.querySelector(`[data-transcript-idx="${transcriptIdx}"]`);
    if (selectedElement && containerRef.current) {
      // Get the container and element positions
      const container = containerRef.current;
      const containerRect = container.getBoundingClientRect();
      const elementRect = selectedElement.getBoundingClientRect();
      
      // Calculate the scroll position needed to center the element
      const elementRelativeTop = elementRect.top - containerRect.top;
      const containerVisibleHeight = container.clientHeight;
      const targetScrollTop = container.scrollTop + elementRelativeTop - (containerVisibleHeight / 2) + (elementRect.height / 2);
      
      // Mark this scroll as programmatic
      programmaticScrollRef.current = true;
      
      // Store the current scroll position
      lastScrollTopRef.current = container.scrollTop;
      
      // Perform the scroll within the container only
      container.scrollTo({
        top: targetScrollTop,
        behavior: 'smooth'
      });
      
      // Reset programmatic flag after animation
      setTimeout(() => {
        programmaticScrollRef.current = false;
      }, 300);
    }
  };

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  // Auto-scroll effect - ALWAYS scroll when timestamp changes unless user manually scrolled during playback
  useEffect(() => {
    // Skip autoscroll if user manually scrolled during playback
    if (isPlaying && userManuallyScrolled.current) {
      return;
    }

    const matchingTranscriptIdx = data.turns.findIndex(turn => {
      const startTime = getTimeInMs(turn.start);
      const endTime = getTimeInMs(turn.end);
      return currentTimeInMs >= startTime && currentTimeInMs <= endTime;
    });

    if (matchingTranscriptIdx !== -1) {
      scrollToTranscript(matchingTranscriptIdx);
      lastTimeRef.current = currentTimeInMs;
    }
  }, [currentTimeInMs, data.turns, isPlaying]);

  if (!data.turns || data.turns.length === 0) {
    return <NoTranscripts />;
  }

  return (
    <div 
      ref={containerRef}
      onScroll={handleScroll}
      className="flex-1 overflow-y-auto space-y-3 py-4 h-full"
    >
      {data.turns.map((turn, idx) => {
        if (turn.text.trim() === '') { return null; }
        
        // Convert start/end times to numbers
        const startTime = getTimeInMs(turn.start);
        const endTime = getTimeInMs(turn.end);
        
        const isActive = isPlaying && 
          currentTimeInMs >= startTime && 
          currentTimeInMs <= endTime;
        
        // Check if this turn has a comment
        const hasComment = data.comments?.some(comment => 
          comment.transcriptIdx === idx
        ) ?? false;

        return (
          <TranscriptRow
            view={view}
            key={`${startTime}-${idx}`}
            turn={turn}
            index={idx}
            isSelected={selectedTranscriptIdx === idx || isActive}
            hasComment={hasComment}
            onClick={() => updateSelectedTranscript?.(idx)}
            sessionId={data.session.session_id}
          />
        );
      })}
    </div>
  );
}; 